import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { comments } from '@/mocks/comments';

const categoryNameMap: Record<string, string> = {
  'mingxing-fanche': '明星翻车',
  'wanghong-tafang': '网红塌房',
  'qiye-neimu': '企业内幕',
  'shishi-baoliao': '实时爆料',
  'heiliao-baoguang': '黑料曝光',
};

const categoryHeroImages: Record<string, string> = {
  'mingxing-fanche': 'https://readdy.ai/api/search-image?query=Red%20carpet%20event%20with%20celebrity%20drama%2C%20paparazzi%20flashes%2C%20glamorous%20chaos%2C%20dramatic%20lighting%2C%20entertainment%20industry%20scene%2C%20luxurious%20atmosphere%2C%20dark%20moody%20tones%2C%20fashionable%20crowd%2C%20film%20premiere%20vibe&width=1920&height=900&seq=cat-hero-mingxing&orientation=landscape',
  'wanghong-tafang': 'https://readdy.ai/api/search-image?query=Social%20media%20influencer%20studio%20with%20ring%20lights%20and%20cameras%2C%20livestreaming%20setup%2C%20dramatic%20lighting%2C%20modern%20content%20creation%20space%2C%20trendy%20atmosphere%2C%20behind%20the%20scenes%20chaos&width=1920&height=900&seq=cat-hero-wanghong&orientation=landscape',
  'qiye-neimu': 'https://readdy.ai/api/search-image?query=Modern%20corporate%20skyscraper%20at%20dusk%2C%20dramatic%20clouds%2C%20business%20district%2C%20glass%20building%20reflections%2C%20mysterious%20corporate%20atmosphere%2C%20professional%20cinematic%20photography&width=1920&height=900&seq=cat-hero-qiye&orientation=landscape',
  'shishi-baoliao': 'https://readdy.ai/api/search-image?query=Breaking%20news%20newsroom%20with%20multiple%20screens%2C%20live%20broadcast%20setup%2C%20dramatic%20red%20lighting%2C%20urgent%20atmosphere%2C%20news%20alert%20theme%2C%20professional%20studio&width=1920&height=900&seq=cat-hero-shishi&orientation=landscape',
  'heiliao-baoguang': 'https://readdy.ai/api/search-image?query=Dark%20investigative%20journalism%20scene%2C%20shadowy%20figure%20with%20documents%2C%20dramatic%20single%20light%20source%2C%20mysterious%20atmosphere%2C%20film%20noir%20style%2C%20truth%20revealing%20theme&width=1920&height=900&seq=cat-hero-heiliao&orientation=landscape',
};

function formatViews(views: number) {
  if (views >= 100000) return `${(views / 10000).toFixed(0)}万阅`;
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万阅`;
  return `${views}阅`;
}

function getHotLabel(score: number) {
  if (score >= 95) return { text: '沸', color: 'bg-red-500' };
  if (score >= 85) return { text: '热', color: 'bg-primary-500' };
  return { text: '新', color: 'bg-accent-500' };
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const catName = category?.name || categoryNameMap[slug || ''] || '未知分类';
  const catArticles = articles.filter((a) => a.category === slug);
  const catComments = comments.filter((c) => {
    const article = articles.find((a) => a.id === c.articleId);
    return article?.category === slug;
  });

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: catName },
  ];

  const leftColComments = catComments.filter((_, i) => i % 3 === 0).slice(0, 2);
  const midColComments = catComments.filter((_, i) => i % 3 === 1).slice(0, 2);
  const rightColComments = catComments.filter((_, i) => i % 3 === 2).slice(0, 2);

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${catName} - 51吃瓜每日大赛`,
    description: category?.description || `${catName}分类下的所有吃瓜爆料文章`,
    url: `https://www.51chigua.com/category/${slug}`,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://www.51chigua.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: catName,
        item: `https://www.51chigua.com/category/${slug}`,
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
      <Navbar />

      <section className="relative w-full h-[320px] md:h-[420px] overflow-hidden">
        <img
          src={categoryHeroImages[slug || ''] || categoryHeroImages['shishi-baoliao']}
          alt={`${catName} - 51吃瓜每日大赛`}
          title={`${catName}分类 - 51吃瓜爆料`}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
        <div className="relative z-10 h-full max-w-7xl mx-auto px-4 md:px-6 flex flex-col justify-end pb-10">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>
          <div className="w-full max-w-3xl">
            <h1 className="text-3xl md:text-5xl font-heading font-black text-white leading-tight mb-2">
              {catName}
            </h1>
            <p className="text-sm md:text-base text-white/70 max-w-xl">
              {category?.description || `${catName}分类下的所有吃瓜爆料文章，实时更新中。`}
            </p>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {catArticles.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center text-foreground-400 mb-4">
                <i className="ri-inbox-line text-4xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {catArticles.map((article) => {
                const hotLabel = getHotLabel(article.hotScore);
                return (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group relative rounded-2xl overflow-hidden aspect-[4/5] bg-foreground-100"
                  >
                    <img
                      src={article.image}
                      alt={article.title}
                      title={`${article.title} - 51吃瓜每日大赛`}
                      className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                    <span className={`absolute top-3 right-3 px-2.5 py-1 text-xs font-label font-medium text-white rounded-lg ${hotLabel.color}`}>
                      {hotLabel.text}
                    </span>
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="text-base font-heading font-bold text-white leading-snug mb-2 line-clamp-2 group-hover:text-primary-300 transition-colors">
                        {article.title}
                      </h3>
                      <div className="flex items-center gap-3 text-xs text-white/60">
                        <span>{catName}</span>
                        <span>·</span>
                        <span>{formatViews(article.views)}</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {catComments.length > 0 && (
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-12">
              <span className="inline-block px-3 py-1 text-xs font-label font-medium bg-accent-100 text-accent-700 rounded-full mb-4">
                精选讨论
              </span>
              <h2 className="text-2xl md:text-4xl font-heading font-black text-foreground-900 leading-tight">
                {catName}热议
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="flex flex-col gap-5">
                {leftColComments.map((c) => (
                  <div key={c.id} className="bg-background-50 rounded-2xl p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                        <img src={c.avatar} alt={c.username} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <div className="text-sm font-label font-medium text-foreground-900">{c.username}</div>
                        <div className="text-xs text-foreground-500">@{c.username}</div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">&ldquo;{c.content}&rdquo;</p>
                    <div className="mt-4 flex items-center gap-4 text-xs text-foreground-500">
                      <span className="flex items-center gap-1"><i className="ri-thumb-up-line"></i> {c.likes}</span>
                      <span>{c.date}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-col gap-5 md:mt-8">
                {midColComments.map((c) => (
                  <div key={c.id} className="bg-background-50 rounded-2xl p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                        <img src={c.avatar} alt={c.username} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <div className="text-sm font-label font-medium text-foreground-900">{c.username}</div>
                        <div className="text-xs text-foreground-500">@{c.username}</div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">&ldquo;{c.content}&rdquo;</p>
                    <div className="mt-4 flex items-center gap-4 text-xs text-foreground-500">
                      <span className="flex items-center gap-1"><i className="ri-thumb-up-line"></i> {c.likes}</span>
                      <span>{c.date}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-col gap-5 md:mt-4">
                {rightColComments.map((c) => (
                  <div key={c.id} className="bg-background-50 rounded-2xl p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                        <img src={c.avatar} alt={c.username} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <div className="text-sm font-label font-medium text-foreground-900">{c.username}</div>
                        <div className="text-xs text-foreground-500">@{c.username}</div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">&ldquo;{c.content}&rdquo;</p>
                    <div className="mt-4 flex items-center gap-4 text-xs text-foreground-500">
                      <span className="flex items-center gap-1"><i className="ri-thumb-up-line"></i> {c.likes}</span>
                      <span>{c.date}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </>
  );
}